import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../context/TenantContext';
import { useBranding } from '../context/BrandingContext';
import { billingService } from '../services/billingService';
import { tasksService } from '../services/tasksService';
import './Sidebar.css';

const Sidebar = () => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTenantMenu, setShowTenantMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [planData, setPlanData] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { t } = useTranslation('common');
  const tenantContext = useTenant();
  const tenant = tenantContext?.tenant;
  const tenants = tenantContext?.tenants || [];
  const setTenant = tenantContext?.setTenant;
  const userRole = tenantContext?.userRole;
  const brandingContext = useBranding();
  const branding = brandingContext?.branding;

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', newState.toString());
    // Dispatch custom event to notify AppLayout
    window.dispatchEvent(new Event('sidebar-toggle'));
    // Close user menu when collapsing
    if (newState) setShowUserMenu(false);
  };

  const handleLogout = async () => {
    const clientId = process.env.REACT_APP_OIDC_CLIENT_ID;
    // Clear local state first, then redirect to Cognito
    await auth.removeUser();
    await auth.clearStaleState();
    await auth.signoutRedirect({
      extraQueryParams: {
        client_id: clientId,
        logout_uri: process.env.REACT_APP_LOGIN_REDIRECT_URL,
      }
    });
  };

  const handleMyAccount = () => {
    setShowUserMenu(false);
    setMobileMenuOpen(false);
    navigate('/account');
  };

  const handleMyTeam = () => {
    setShowUserMenu(false);
    setMobileMenuOpen(false);
    navigate('/team');
  };

  const handleAPIAccess = () => {
    setShowUserMenu(false);
    setMobileMenuOpen(false);
    navigate('/api-access');
  };

  // Fetch plan data on mount for billing info display
  useEffect(() => {
    if (auth.user?.id_token && tenant) {
      billingService.getMyPlan(auth.user.id_token, tenant)
        .then(data => setPlanData(data))
        .catch(() => setPlanData(null));
    }
  }, [auth.user?.id_token, tenant]);

  // My Tasks pending count badge (plan 88). Non-blocking: a failed fetch
  // just hides the badge. Refreshes on route change and window focus.
  const [tasksCount, setTasksCount] = useState(0);
  useEffect(() => {
    const token = auth.user?.id_token;
    if (!token || !tenant) return;
    let cancelled = false;
    const refresh = () => {
      tasksService.getSummary(token, tenant)
        .then(data => { if (!cancelled) setTasksCount(data?.total_tasks || 0); })
        .catch(() => { if (!cancelled) setTasksCount(0); });
    };
    refresh();
    window.addEventListener('focus', refresh);
    // Plan 98 REQ-15: an inline completion on /tasks dispatches this event so
    // the badge, box counts, and rows stay consistent without a reload.
    window.addEventListener('stackbd:tasks-count-changed', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('stackbd:tasks-count-changed', refresh);
    };
  }, [auth.user?.id_token, tenant, location.pathname]);

  const userEmail = auth.user?.profile?.email;
  const isImpersonating = !!tenantContext?.isImpersonating;
  const DEV_EMAIL = 'tom+dev7@stackbd.com';
  const WILL_EMAIL = 'will@stackbd.com';
  const OWNER_EMAILS = [DEV_EMAIL, WILL_EMAIL];

  // StackBD-internal account gates are scoped to the real (non-impersonated)
  // session: internal/research tools should disappear when viewing as a customer.
  const isDevAccount = !isImpersonating && userEmail?.toLowerCase() === DEV_EMAIL;
  const isOwnerAccount = !isImpersonating && OWNER_EMAILS.includes(userEmail?.toLowerCase());

  // Grouped by usage with dividers (dividerBefore starts a new group):
  //   1. daily actions — Tasks, Record, Funnel, Analytics
  //   2. outreach/content — Lists, Messages, Engagements, Templates
  //   3. account/compliance settings — Limits, Do Not Contact, Human Verification
  //   then FAQ (help) last. Keep new items in the matching group.
  const menuItems = [
    {
      path: '/tasks',
      label: t('nav.tasks'),
      testId: 'sidebar-tasks-link',
      countBadge: tasksCount,
      countBadgeTestId: 'sidebar-tasks-badge',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      )
    },
    {
      path: '/recording',
      label: t('nav.record'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
        </svg>
      )
    },
    {
      path: '/funnel',
      label: t('nav.funnel'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
        </svg>
      )
    },
    {
      path: '/analytics',
      label: t('nav.analytics'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      )
    },
    {
      path: '/campaigns',
      label: t('nav.lists'),
      dividerBefore: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6"/>
          <circle cx="12" cy="12" r="2"/>
        </svg>
      )
    },
    {
      path: '/messages',
      label: t('nav.messages'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )
    },
    {
      path: '/engagements',
      label: t('nav.engagements'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      )
    },
    {
      path: '/scripts',
      label: t('nav.templates'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      )
    },
    {
      path: '/limits',
      label: t('nav.limits'),
      dividerBefore: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <path d="M3 9h18"/>
          <path d="M9 21V9"/>
        </svg>
      )
    },
    {
      path: '/account/dnc',
      label: 'Do Not Contact',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
      )
    },
    {
      // Plan 103, REQ-01 — Human Verification (visible to all authenticated users).
      path: '/verification',
      label: t('nav.verification', 'Human Verification'),
      testId: 'hv-sidebar-entry',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      )
    },
    {
      path: '/faq',
      label: t('nav.faq'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      )
    },
  ];

  // Admin, Testing — only visible to dev research account
  const stackBDMenuItems = isDevAccount ? [
    {
      path: '/admin/simulate',
      label: 'Testing',
      badge: 'SADMIN',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
        </svg>
      )
    },
  ] : [];

  // Coordination + Signals visible to owner accounts
  const ownerMenuItems = [
    ...(isOwnerAccount ? [
      {
        path: '/posts',
        label: t('nav.content', 'Content'),
        badge: 'RESEARCH',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
          </svg>
        )
      },
      {
        path: '/agent',
        label: 'Agent',
        badge: 'RESEARCH',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            {/* Stylized brain: two hemispheres + central fissure + a few gyri */}
            <path d="M9.5 3a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 0 4 3 3 0 0 0 2 5 3 3 0 0 0 3 3 2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" />
            <path d="M14.5 3a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1 0 4 3 3 0 0 1-2 5 3 3 0 0 1-3 3 2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
            <path d="M12 5v14" />
            <path d="M6.5 8.5a2 2 0 0 0 1.5 1.5" />
            <path d="M17.5 8.5a2 2 0 0 1-1.5 1.5" />
            <path d="M6.5 15.5a2 2 0 0 1 1.5-1.5" />
            <path d="M17.5 15.5a2 2 0 0 0-1.5-1.5" />
          </svg>
        )
      },
      {
        path: '/coordination',
        label: t('nav.coordination'),
        badge: 'RESEARCH',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <circle cx="5" cy="6" r="2" />
            <circle cx="19" cy="6" r="2" />
            <circle cx="5" cy="18" r="2" />
            <circle cx="19" cy="18" r="2" />
            <line x1="9.5" y1="10.5" x2="6.5" y2="7.5" />
            <line x1="14.5" y1="10.5" x2="17.5" y2="7.5" />
            <line x1="9.5" y1="13.5" x2="6.5" y2="16.5" />
            <line x1="14.5" y1="13.5" x2="17.5" y2="16.5" />
          </svg>
        )
      },
      {
        path: '/signals',
        label: t('nav.signals'),
        badge: 'RESEARCH',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20v-4" />
            <path d="M12 12V8" />
            <circle cx="12" cy="14" r="2" fill="currentColor" stroke="none" />
            <path d="M8.5 17a5 5 0 0 1 0-6" />
            <path d="M15.5 17a5 5 0 0 0 0-6" />
            <path d="M5.5 19.5a9 9 0 0 1 0-11" />
            <path d="M18.5 19.5a9 9 0 0 0 0-11" />
          </svg>
        )
      },
      {
        path: '/graph',
        label: 'Graph',
        badge: 'RESEARCH',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="6" y1="7" x2="12" y2="17" />
            <line x1="12" y1="17" x2="18" y2="7" />
            <line x1="6" y1="7" x2="18" y2="7" />
            <circle cx="6" cy="7" r="2.5" fill="currentColor" stroke="none" />
            <circle cx="18" cy="7" r="2.5" fill="currentColor" stroke="none" />
            <circle cx="12" cy="17" r="2.5" fill="currentColor" stroke="none" />
          </svg>
        )
      },
      {
        path: '/admin',
        label: t('nav.admin'),
        badge: 'SADMIN',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        )
      },
    ] : []),
  ];

  // Admin-only menu items
  const adminMenuItems = [
    // Contacts, Companies, and Videos only visible to tom+dev7@stackbd.com
    ...(isDevAccount ? [
      {
        path: '/companies',
        label: t('nav.companies'),
        badge: 'SADMIN',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" />
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            <line x1="12" y1="12" x2="12" y2="12.01" />
          </svg>
        )
      },
      {
        path: '/contacts',
        label: t('nav.contacts'),
        badge: 'SADMIN',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        )
      },
      {
        path: '/video-archive',
        label: t('nav.videos'),
        badge: 'SADMIN',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
          </svg>
        )
      },
      {
        path: '/chat',
        label: t('nav.chat'),
        badge: 'SADMIN',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )
      },
      {
        path: '/petri',
        label: t('nav.petri'),
        badge: 'SADMIN',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6" cy="6" r="2.5" />
            <circle cx="18" cy="6" r="2.5" />
            <circle cx="6" cy="18" r="2.5" />
            <circle cx="18" cy="18" r="2.5" />
            <line x1="8.5" y1="6" x2="15.5" y2="6" />
            <line x1="6" y1="8.5" x2="6" y2="15.5" />
            <line x1="18" y1="8.5" x2="18" y2="15.5" />
            <line x1="8.5" y1="18" x2="15.5" y2="18" />
          </svg>
        )
      },
    ] : []),
  ];

  // Bottom nav items (Agent, Quantum) - pushed to bottom of sidebar
  const bottomMenuItems = isDevAccount ? [
    {
      path: '/agent-training',
      label: 'Agent Training',
      badge: 'SADMIN',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20v-1a8 8 0 0 1 16 0v1" />
        </svg>
      )
    },
    {
      path: '/qml',
      label: t('nav.quantum'),
      badge: 'SADMIN',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <circle cx="12" cy="12" r="8" opacity="0.4" />
          <line x1="12" y1="2" x2="12" y2="5" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="2" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="22" y2="12" />
        </svg>
      )
    },
  ] : [];

  // Close mobile menu when navigating
  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 18L18 6M6 6l12 12"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12h16M4 6h16M4 18h16"/>
          </svg>
        )}
      </button>

      {/* Overlay for mobile */}
      <div
        className={`sidebar-overlay ${mobileMenuOpen ? 'visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <div className={`video-sidebar ${mobileMenuOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="brand">
            <div className="brand-logo">
              <img
                src={branding?.logo_url || '/logo192.png'}
                alt={branding?.app_name || 'StackBD'}
                data-testid="sidebar-logo"
                onError={(e) => { e.currentTarget.src = '/logo192.png'; }}
              />
            </div>
          </div>
          <button className="collapse-toggle" onClick={toggleCollapse} title={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {isCollapsed ? (
                <path d="M9 18l6-6-6-6" />
              ) : (
                <path d="M15 18l-6-6 6-6" />
              )}
            </svg>
          </button>
        </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path === '/funnel' && location.pathname === '/');
          return (
            <React.Fragment key={item.path}>
              {item.dividerBefore && <div className="nav-divider" aria-hidden="true" />}
            <NavLink
              to={item.path}
              onClick={handleNavClick}
              data-testid={item.testId}
              className={`nav-item ${isActive ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {item.badge && <span className={`nav-badge nav-badge-${item.badge.toLowerCase()}`}>{item.badge}</span>}
              {item.countBadge > 0 && (
                <span className="nav-count-badge" data-testid={item.countBadgeTestId}>{item.countBadge}</span>
              )}
            </NavLink>
            </React.Fragment>
          );
        })}
        {ownerMenuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
            {item.badge && <span className={`nav-badge nav-badge-${item.badge.toLowerCase()}`}>{item.badge}</span>}
          </NavLink>
        ))}
        {stackBDMenuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
            {item.badge && <span className={`nav-badge nav-badge-${item.badge.toLowerCase()}`}>{item.badge}</span>}
          </NavLink>
        ))}
        {adminMenuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
            {item.badge && <span className={`nav-badge nav-badge-${item.badge.toLowerCase()}`}>{item.badge}</span>}
          </NavLink>
        ))}
        {bottomMenuItems.length > 0 && (
          <>
            <div className="nav-spacer" />
            {bottomMenuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {item.badge && <span className={`nav-badge nav-badge-${item.badge.toLowerCase()}`}>{item.badge}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        {/* Impersonation Banner */}
        {tenantContext?.isImpersonating && (
          <div className="impersonation-banner">
            <div className="impersonation-info">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span className="impersonation-text">{t('sidebar.viewingAs')} {tenantContext.impersonatingUser}</span>
            </div>
            <button className="impersonation-exit" onClick={() => {
              tenantContext.stopImpersonation();
              navigate('/admin');
            }}>{t('sidebar.exit')}</button>
          </div>
        )}

        {/* Tenant Selector - only shown if user has multiple tenants and not impersonating */}
        {tenants.length > 1 && !tenantContext?.isImpersonating && (
          <div className="tenant-selector-container">
            <div
              className="tenant-selector"
              onClick={() => {
                setShowTenantMenu(!showTenantMenu);
                setShowUserMenu(false);
              }}
            >
              <div className="tenant-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <div className="tenant-info">
                <div className="tenant-label">{t('sidebar.workspace')}</div>
                <div className="tenant-name">{tenant || t('sidebar.select')}</div>
              </div>
              <div className="dropdown-arrow">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </div>
            </div>

            {showTenantMenu && (
              <div className="tenant-menu">
                {tenants.map((t) => (
                  <button
                    key={t}
                    className={`tenant-menu-item ${t === tenant ? 'active' : ''}`}
                    onClick={() => {
                      setTenant(t);
                      setShowTenantMenu(false);
                    }}
                  >
                    <span className="tenant-menu-name">{t}</span>
                    {t === tenant && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="check-icon">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="user-profile-container">
          <div
            className="user-profile"
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowTenantMenu(false);
            }}
          >
            <div className="user-info">
              <div className="user-name">{auth.user?.profile?.email || 'User'}</div>
            </div>
            <div className="dropdown-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </div>
          </div>
          
          {showUserMenu && (
            <div className="user-menu">
              <button className="menu-item" onClick={handleMyAccount}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4"/>
                  <path d="M4 20v-1a8 8 0 0 1 16 0v1"/>
                </svg>
                {t('userMenu.myAccount')}
              </button>
              {(userRole === 'owner' || userRole === 'admin') && (
                <button className="menu-item" onClick={handleMyTeam}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  {t('userMenu.myTeam')}
                </button>
              )}
              {(userRole === 'owner' || userRole === 'admin') && (
                <button className="menu-item" onClick={handleAPIAccess}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  API Access
                </button>
              )}
              <button className="menu-item" onClick={handleLogout}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                {t('userMenu.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;